import * as d3 from 'd3';
import ReactDOMServer from 'react-dom/server';
import { black, white } from '../theme/colors';
import { getHeatingMethodName, areCarsEV } from './netZeroHelper';
import JaggedStrip from '../assets/svg/jagged-strip.svg';

export const chartHeight = 340;

const top = 30;
const bottom = 260 + top;

const columnCount = 4;
const svgMarginWidth = 54;
const chartMarginWidth = 20;
const smallWidthThreshold = 520;

export const CHART_STATES = {
  NOW: 'NOW',
  SOLAR: 'SOLAR',
  INSULATE: 'INSULATE',
  EV: 'EV',
  HEAT: 'HEAT',
  GRID: 'GRID',
};

const columnLabels = {
  electricity: {
    full: 'Electricity',
    short: 'Elec.',
  },
  heating: {
    full: 'Heating',
    short: 'Heat',
  },
  car: {
    full: 'Car',
    short: 'Car',
  },
  solar: {
    full: 'Solar',
    short: 'Solar',
  },
};

export const addGradient = (defs, name, start, stop) => {
  const gradient = defs
    .append('linearGradient')
    .attr('id', name)
    .attr('gradientTransform', 'rotate(-20)');
  gradient.append('stop').attr('stop-color', start).attr('offset', '0%');
  gradient.append('stop').attr('stop-color', stop).attr('offset', '100%');
};

const getBarWidth = canvasWidth =>
  (canvasWidth -
    (getSvgLeftMargin(canvasWidth) +
      getSvgRightMargin(canvasWidth) +
      chartMarginWidth * 2 +
      (chartMarginWidth / 2) * (columnCount - 1))) /
  columnCount;

const getBarXPos = (columnIndex, barWidth, canvasWidth) =>
  getSvgLeftMargin(canvasWidth) +
  chartMarginWidth +
  (columnIndex * barWidth + (chartMarginWidth / 2) * columnIndex);

const getTextXPos = (i, barWidth, canvasWidth) =>
  getBarXPos(i, barWidth, canvasWidth) + barWidth / 2;

export const getScaleHeight = range => (bottom - top) / range;

export const getSvgLeftMargin = canvasWidth =>
  canvasWidth > smallWidthThreshold ? svgMarginWidth : svgMarginWidth / 1.5;

export const getSvgRightMargin = canvasWidth =>
  canvasWidth > smallWidthThreshold ? svgMarginWidth : svgMarginWidth / 5;

export const addScaleLines = (svg, range, tHeight, max, canvasWidth) => {
  let zeroY;
  const svgLeftMargin = getSvgLeftMargin(canvasWidth);
  const svgRightMargin = getSvgRightMargin(canvasWidth);
  for (let i = 0; i <= range; i++) {
    const yPos = top + i * tHeight;
    const current = max - i;
    if (current === 0) {
      zeroY = yPos;
      svg
        .append('line')
        .style('stroke', '#afb6b9')
        .style('stroke-width', 1)
        .attr('class', 'zero-y-line')
        .attr('x1', svgLeftMargin)
        .attr('y1', yPos)
        .attr('x2', canvasWidth - svgRightMargin)
        .attr('y2', yPos);
    } else {
      svg
        .append('line')
        .style('stroke', '#afb6b9')
        .style('stroke-width', 1)
        .style('stroke-opacity', 0.5)
        .style('stroke-dasharray', '5,5')
        .attr('x1', svgLeftMargin)
        .attr('y1', yPos)
        .attr('x2', canvasWidth - svgRightMargin)
        .attr('y2', yPos);
    }
    // add label to background lines
    const lineLabel = current === 0 ? current : `${current}T`;
    svg
      .append('text')
      .attr('class', 'scale-text')
      .attr('x', svgLeftMargin - 5)
      .attr('y', yPos + 4)
      .attr('text-anchor', 'end')
      .attr('width', 15)
      .attr('font-size', '12')
      .text(lineLabel);
  }
  return zeroY;
};

const getColumnLabel = (columnTag, canvasWidth) => {
  const size = canvasWidth > smallWidthThreshold ? 'full' : 'short';
  return columnLabels[columnTag][size];
};

export const addColumnNames = (svg, canvasWidth) => {
  const columns = ['electricity', 'heating', 'car', 'solar'];
  const barWidth = getBarWidth(canvasWidth);
  for (let i = 0; i < columns.length; i++) {
    const columnTag = columns[i];
    svg
      .append('text')
      .attr('x', getTextXPos(i, barWidth, canvasWidth))
      .attr('y', bottom + 30)
      .attr('text-anchor', 'middle')
      .attr('width', barWidth)
      .attr('font-size', '12')
      .attr('class', 'column-name')
      .attr('data-index', i)
      .attr('data-column-tag', columnTag)
      .text(getColumnLabel(columnTag, canvasWidth));
  }
};

const addBar = (
  svg,
  id,
  barData,
  yPos,
  height,
  gradient,
  opacity,
  columnIndex,
  tooltipTitle,
  shortTitle,
  canvasWidth,
) => {
  const barWidth = getBarWidth(canvasWidth);
  const xPos = getBarXPos(columnIndex, barWidth, canvasWidth);
  svg
    .append('rect')
    .attr('id', id)
    .attr('class', `scale-bar ${shortTitle}-bar`)
    .attr(
      'data-json',
      JSON.stringify({
        ...barData,
        tooltipTitle,
        shortTitle,
        columnIndex,
      }),
    )
    .attr('x', xPos)
    .attr('y', yPos)
    .attr('fill', `url(#${gradient})`)
    .attr('rx', 2)
    .attr('width', barWidth)
    .attr('height', height)
    .style('opacity', opacity)
    .on('mouseover', barMouseOver)
    .on('mouseleave', barMouseLeave);
  // only draw jagged strip for the main bar
  if (!height) {
    svg
      .append('image')
      .attr('id', `${id}-jagged-strip`)
      .attr('class', `jagged-strip ${shortTitle}-jagged-stripe`)
      .attr('x', xPos)
      .attr('width', barWidth)
      .attr('xlink:href', JaggedStrip);
  }
};

const getNotationStartYPos = (position, zeroY) =>
  position === notationPositions.below ? zeroY + 20 : zeroY - 70;

const getNotationEndYPos = (position, zeroY) =>
  position === notationPositions.below ? zeroY + 10 : zeroY - 60;

const getPointerGroupYPos = position =>
  position === notationPositions.below ? -2 : 52;

export const hideNotations = ids =>
  ids.forEach(id => {
    d3.select(`#${id}-key`).remove();
    d3.select(`#${id}`).remove();
  });

export const notationTypes = {
  solar: 'solar',
  elec: 'elec',
  heat: 'heat',
  car: 'car',
  grid: 'grid',
};

const appendNotationTextLarge = (group, barWidth, yPos, text) =>
  group
    .append('text')
    .attr('class', 'notation-text-large')
    .attr('width', barWidth)
    .attr('text-anchor', 'middle')
    .attr('x', barWidth / 2)
    .attr('y', yPos)
    .style('fill', white)
    .style('font-size', '11px')
    .text(text);

const appendNotationTextSmall = (
  group,
  barWidth,
  notationText,
  keyText,
  position,
  id,
) => {
  group
    .append('text')
    .attr('class', 'notation-text-small')
    .attr('width', barWidth)
    .attr('text-anchor', 'middle')
    .attr('x', barWidth / 2)
    .attr('y', position === notationPositions.below ? 18 : 38)
    .style('fill', white)
    .style('font-size', '11px')
    .style('font-weight', 'bold')
    .text(notationText);

  const div = d3.select('#notation-key').append('div').attr('id', `${id}-key`);
  div.append('strong').text(notationText);
  div.append('span').text(keyText);
};

const addNotationText = (
  notationType,
  group,
  barWidth,
  position,
  id,
  heatingMethod,
  cars,
) => {
  switch (notationType) {
    default:
      appendNotationTextLarge(group, barWidth, 20, 'Solar adds negative');
      appendNotationTextLarge(group, barWidth, 38, 'emissions');
      appendNotationTextSmall(
        group,
        barWidth,
        '1.',
        'Solar adds negative emissions',
        position,
        id,
      );
      break;
    case notationTypes.elec:
      appendNotationTextLarge(group, barWidth, 28, 'Small fall in electricity');
      appendNotationTextSmall(
        group,
        barWidth,
        '2.',
        'Small fall in electricity',
        position,
        id,
      );
      break;
    case notationTypes.heat:
      appendNotationTextLarge(
        group,
        barWidth,
        28,
        `${getHeatingMethodName(heatingMethod)} use falls`,
      );
      appendNotationTextSmall(
        group,
        barWidth,
        '3.',
        `${getHeatingMethodName(heatingMethod)} use falls`,
        position,
        id,
      );
      break;
    case notationTypes.car:
      if (areCarsEV(cars)) {
        appendNotationTextLarge(group, barWidth, 14, 'You are already');
        appendNotationTextLarge(group, barWidth, 28, 'electric');
        appendNotationTextSmall(
          group,
          barWidth,
          '4.',
          'You are already electric',
          position,
          id,
        );
      } else {
        appendNotationTextLarge(group, barWidth, 14, 'Big drop from moving');
        appendNotationTextLarge(group, barWidth, 28, 'from petrol/diesel');
        appendNotationTextLarge(group, barWidth, 42, 'to electric');
        appendNotationTextSmall(
          group,
          barWidth,
          '4.',
          'Big drop from moving from petrol/diesel to electric',
          position,
          id,
        );
      }
      break;
    case notationTypes.grid:
      appendNotationTextLarge(group, barWidth, 14, 'Emissions from');
      appendNotationTextLarge(group, barWidth, 28, 'heating drop by');
      appendNotationTextLarge(group, barWidth, 42, 'over two-thirds');
      appendNotationTextSmall(
        group,
        barWidth,
        '5.',
        'Emissions from heating drop by over two-thirds',
        position,
        id,
      );
      break;
  }
};

export const showNotation = (
  id,
  zeroY,
  columnIndex,
  canvasWidth,
  position,
  notationType,
  heatingMethod,
  cars,
) => {
  if (!d3.select(`#${id}`).empty()) {
    return;
  }

  const svg = d3.select('#netzero-chart');
  if (!position) {
    position = notationPositions.below;
  }
  const barWidth = getBarWidth(canvasWidth);
  const xPos = getBarXPos(columnIndex, barWidth, canvasWidth);
  const yStartPos = getNotationStartYPos(position, zeroY);
  const yEndPos = getNotationEndYPos(position, zeroY);
  const group = svg
    .append('g')
    .attr('id', `${id}`)
    .attr('class', 'notation-group')
    .attr(
      'data-json',
      JSON.stringify({
        columnIndex,
        zeroY,
        position,
      }),
    )
    .attr('transform', `translate(${xPos}, ${yStartPos})`)
    .attr('opacity', 0);

  // append a group for the pointer
  const pointerGroup = group
    .append('g')
    .attr('id', `${id}-pointer-group`)
    .attr(
      'transform',
      d => `translate(${barWidth / 2}, ${getPointerGroupYPos(position)})`,
    );

  pointerGroup
    .append('path')
    .attr('id', `${id}-pointer`)
    .attr('d', d3.symbol().type(d3.symbolTriangle).size(30))
    .attr('fill', black)
    .attr(
      'transform',
      `rotate(${position === notationPositions.below ? 0 : 180})`,
    );

  group
    .append('rect')
    .attr('id', `${id}-background-large`)
    .attr('class', 'notation-background-large')
    .attr('fill', black)
    .attr('rx', 2)
    .attr('width', barWidth)
    .attr('height', 50);
  group
    .append('rect')
    .attr('id', `${id}-background-small`)
    .attr('class', 'notation-background-small')
    .attr('fill', black)
    .attr('rx', 2)
    .attr('x', barWidth / 2 - 15)
    .attr('y', position === notationPositions.below ? 0 : 20)
    .attr('width', 30)
    .attr('height', 30);

  addNotationText(
    notationType,
    group,
    barWidth,
    position,
    id,
    heatingMethod,
    cars,
  );

  group
    .transition()
    .duration(250)
    .ease(d3.easeQuadInOut)
    .attr('transform', `translate(${xPos}, ${yEndPos})`)
    .attr('opacity', 1);
};

export const addBarGroup = (
  svg,
  data,
  zeroY,
  shortTitle,
  columnIndex,
  tooltipTitle,
  canvasWidth,
  hideOriginal,
  notationPosition,
) => {
  if (!hideOriginal) {
    addBar(
      svg,
      `max-${shortTitle}-bar`,
      data,
      zeroY - data.originalHeight,
      data.originalHeight,
      `${shortTitle}-gradient`,
      0.2,
      columnIndex,
      tooltipTitle,
      shortTitle,
      canvasWidth,
    );
  }
  addBar(
    svg,
    `${shortTitle}-bar`,
    data,
    zeroY,
    0,
    `${shortTitle}-gradient`,
    1,
    columnIndex,
    tooltipTitle,
    shortTitle,
    canvasWidth,
  );
};

export const columns = {
  first: 0,
  second: 1,
  third: 2,
  fourth: 3,
};

export const notationPositions = {
  above: 'above',
  below: 'below',
};

export const resizeScaleLines = canvasWidth => {
  const svgLeftMargin = getSvgLeftMargin(canvasWidth);
  d3.selectAll('line')
    .attr('x1', svgLeftMargin)
    .attr('x2', canvasWidth - getSvgRightMargin(canvasWidth));
  d3.selectAll('.scale-text').attr('x', svgLeftMargin - 5);
};

export const resizeBars = canvasWidth =>
  d3.selectAll('.scale-bar').each(function (d) {
    const barWidth = getBarWidth(canvasWidth);
    // TODO: check whether we need 'this' here
    // eslint-disable-next-line no-invalid-this
    const bar = d3.select(this);
    const data = JSON.parse(bar.attr('data-json'));
    const barXPos = getBarXPos(data.columnIndex, barWidth, canvasWidth);
    bar.attr('width', barWidth).attr('x', barXPos);
    d3.select(`#${bar.attr('id')}-jagged-strip`)
      .attr('width', barWidth)
      .attr('x', barXPos);
  });

export const resizeColumnNames = canvasWidth =>
  d3.selectAll('.column-name').each(function (d) {
    const barWidth = getBarWidth(canvasWidth);
    // TODO: check whether we need 'this' here
    // eslint-disable-next-line no-invalid-this
    const column = d3.select(this);
    const index = column.attr('data-index');
    const columnTag = column.attr('data-column-tag');
    column
      .attr('x', getTextXPos(index, barWidth, canvasWidth))
      .text(getColumnLabel(columnTag, canvasWidth));
  });

export const resizeNotations = canvasWidth => {
  const barWidth = getBarWidth(canvasWidth);
  d3.selectAll('.notation-group').each(function (d) {
    // TODO: check whether we need 'this' here
    // eslint-disable-next-line no-invalid-this
    const data = JSON.parse(d3.select(this).attr('data-json'));
    // TODO: check whether we need 'this' here
    // eslint-disable-next-line no-invalid-this
    const current = d3.select(this);
    const xPos = getBarXPos(data.columnIndex, barWidth, canvasWidth);
    const yPos = getNotationEndYPos(data.position, data.zeroY);
    current.attr('transform', `translate(${xPos}, ${yPos})`);

    d3.select(`#${current.attr('id')}-background-large`).attr(
      'width',
      barWidth,
    );

    d3.select(`#${current.attr('id')}-background-small`).attr(
      'x',
      barWidth / 2 - 15,
    );

    d3.select(`#${current.attr('id')}-pointer-group`).attr(
      'transform',
      d => `translate(${barWidth / 2}, ${getPointerGroupYPos(data.position)})`,
    );
  });
  d3.selectAll('.notation-text-large').each(function (d) {
    // TODO: check whether we need 'this' here
    // eslint-disable-next-line no-invalid-this
    d3.select(this).attr('x', barWidth / 2);
  });
  d3.selectAll('.notation-text-small').each(function (d) {
    // TODO: check whether we need 'this' here
    // eslint-disable-next-line no-invalid-this
    d3.select(this).attr('x', barWidth / 2);
  });
};

export const animateBar = (selector, updatedBarData, zeroY) => {
  const element = d3.select(`#${selector}`);
  const barData = JSON.parse(element.attr('data-json'));
  let { currentHeight } = updatedBarData;
  let yPos;
  if (currentHeight < 0) {
    yPos = zeroY;
    currentHeight = -currentHeight;
  } else {
    yPos = zeroY - currentHeight;
  }
  element
    .transition()
    .duration(250)
    .ease(d3.easeQuadInOut)
    .attr(
      'data-json',
      JSON.stringify({
        ...barData,
        ...updatedBarData,
      }),
    )
    .attr('height', currentHeight)
    .attr('y', yPos)
    .on('end', () => {
      if (currentHeight < zeroY) {
        d3.select(`#${element.attr('id')}-jagged-strip`).style('opacity', 0);
      }
    });
  d3.select(`#max-${barData.shortTitle}-bar`).attr(
    'data-json',
    JSON.stringify({
      ...barData,
      ...updatedBarData,
    }),
  );
  if (currentHeight > zeroY) {
    d3.select(`#${element.attr('id')}-jagged-strip`).style('opacity', 1);
  }
};

const barMouseOver = event => {
  const element = d3.select(event.target);
  const data = JSON.parse(element.attr('data-json'));
  const tooltipNewContent = getTooltip(data);
  const tooltipCurrent = document.getElementById('tooltip');

  if (tooltipNewContent !== tooltipCurrent.innerHTML) {
    tooltipCurrent.innerHTML = tooltipNewContent;
  }

  d3.select('#tooltip')
    .style('display', 'block')
    .transition()
    .duration(200)
    .style('opacity', 1);
};

const getTooltip = data => {
  return ReactDOMServer.renderToString(
    <div className={data.shortTitle}>
      <div className="title">{data.tooltipTitle}</div>
      {data?.originalTonnes ? (
        <>
          <div className="d-flex flex-row">
            <i className="now" /> Your home 2020:&nbsp;
            <strong>{data.originalTonnes.toFixed(2).replace('.00', '')}</strong>
            &nbsp;tonnes
            <br />
          </div>
          {data.originalTonnes !== data.currentTonnes && (
            <div className="d-flex flex-row">
              <i className="changes" /> With changes:&nbsp;
              <strong>
                {data.currentTonnes.toFixed(2).replace('.00', '')}
              </strong>
              &nbsp;tonnes
            </div>
          )}
        </>
      ) : (
        <span className="now">
          Negative emissions:{' '}
          <strong>{-data.currentTonnes.toFixed(2).replace('.00', '')}</strong>
          &nbsp;tonnes
        </span>
      )}
    </div>,
  );
};

const barMouseLeave = () => {
  d3.select('#tooltip')
    .transition()
    .duration(200)
    .style('opacity', 0)
    .style('height', 'auto')
    .on('end', () => d3.select('#tooltip').style('display', 'none'));
};
